import api from './api';

export const cmsService = {
  // ===== HERO IMAGES =====
  getHeroImages: async () => {
    const response = await api.get('/cms/heroes');
    return response.data;
  },

  getHeroImage: async (pageId) => {
    const response = await api.get(`/cms/heroes/${pageId}`);
    return response.data;
  },

  uploadHeroImage: async (formData) => {
    const response = await api.post('/cms/heroes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteHeroImage: async (pageId) => {
    const response = await api.delete(`/cms/heroes/${pageId}`);
    return response.data;
  },

  // ===== LABELS =====
  getLabels: async (type) => {
    const response = await api.get(`/cms/labels${type ? `?type=${type}` : ''}`);
    return response.data;
  },

  createLabel: async (data) => {
    const response = await api.post('/cms/labels', data);
    return response.data;
  },

  updateLabel: async (id, data) => {
    const response = await api.put(`/cms/labels/${id}`, data);
    return response.data;
  },

  deleteLabel: async (id) => {
    const response = await api.delete(`/cms/labels/${id}`);
    return response.data;
  },

  // ===== REELS (Labels with photo counts) =====
  getReels: async () => {
    const response = await api.get('/cms/labels/reels');
    return response.data;
  },

  deleteReel: async (labelId, deleteLabel = false) => {
    const response = await api.delete(`/cms/labels/${labelId}/reel?deleteLabel=${deleteLabel}`);
    return response.data;
  },

  // ===== GALLERY =====
  getGallery: async (params = {}) => {
    // FIX: Filter out undefined/null/empty values to prevent "label=undefined" in query string
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await api.get(`/cms/gallery${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  uploadGalleryItem: async (formData) => {
    const response = await api.post('/cms/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  updateGalleryItem: async (id, data) => {
    const response = await api.put(`/cms/gallery/${id}`, data);
    return response.data;
  },

  deleteGalleryItem: async (id) => {
    const response = await api.delete(`/cms/gallery/${id}`);
    return response.data;
  },

  // ===== EVENTS =====
  getEvents: async (params = {}) => {
    // FIX: Filter out undefined/null/empty values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v != null && v !== '')
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await api.get(`/cms/events${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  createEvent: async (formData) => {
    const response = await api.post('/cms/events', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  updateEvent: async (id, formData) => {
    const response = await api.put(`/cms/events/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteEvent: async (id) => {
    const response = await api.delete(`/cms/events/${id}`);
    return response.data;
  },

  // ===== TESTIMONIALS =====
  getTestimonials: async () => {
    const response = await api.get('/testimonials');
    return response.data;
  },

  getTestimonial: async (id) => {
    const response = await api.get(`/testimonials/${id}`);
    return response.data;
  },

  createTestimonial: async (formData) => {
    const response = await api.post('/testimonials', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  updateTestimonial: async (id, formData) => {
    const response = await api.put(`/testimonials/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deleteTestimonial: async (id) => {
    const response = await api.delete(`/testimonials/${id}`);
    return response.data;
  },

  // ===== PAGE CONTENT =====
  getAllContent: async () => {
    const response = await api.get('/cms/content');
    return response.data;
  },

  getPageContent: async (page) => {
    const response = await api.get(`/cms/content/${page}`);
    return response.data;
  },

  updatePageContent: async (page, section, data) => {
    const response = await api.put(`/cms/content/${page}/${section}`, data);
    return response.data;
  },

  batchUpdatePageContent: async (page, sections) => {
    const response = await api.put(`/cms/content/${page}`, sections);
    return response.data;
  },

  deletePageContent: async (page, section) => {
    const response = await api.delete(`/cms/content/${page}/${section}`);
    return response.data;
  },

  uploadSectionImage: async (page, section, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/cms/content/${page}/${section}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // ===== INTRO VIDEO =====
  getIntroVideo: async () => {
    const response = await api.get('/cms/intro-video');
    return response.data;
  },

  updateIntroVideo: async (data) => {
    const response = await api.put('/cms/intro-video', data);
    return response.data;
  },

  uploadIntroVideo: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('video', file);
    if (options.autoplay !== undefined) formData.append('autoplay', options.autoplay);
    if (options.show_skip !== undefined) formData.append('show_skip', options.show_skip);
    const response = await api.post('/cms/intro-video/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // ===== PAGE VIDEO =====
  uploadPageVideo: async (page, file, videoTitle) => {
    const formData = new FormData();
    formData.append('video', file);
    if (videoTitle) formData.append('video_title', videoTitle);
    const response = await api.post(`/cms/content/${page}/video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deletePageVideo: async (page) => {
    const response = await api.delete(`/cms/content/${page}/video`);
    return response.data;
  },

  // ===== FILE UPLOAD =====
  uploadFile: async (file, folder = 'uploads') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const response = await api.post('/cms/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export default cmsService;
